import {
  LayoutDashboard, Building2, Users, Target, Briefcase, Package, FileText, ShoppingCart,
  CheckSquare, Activity, BarChart3, ClipboardCheck, Settings, Moon, Sun, Keyboard, Plus, PenLine,
} from 'lucide-react';

// What the palette can do besides finding records. Built per render from the
// person's modules and permissions, so a hidden module never appears.
//
// Creating a record goes to the list page with ?new=1, which opens that
// page's create modal — one path for creation, no second form to maintain.

export function buildCommands({ navigate, isModuleVisible, canDo, hasPermission, isAdmin, theme, toggleTheme, openShortcuts, askAssistant }) {
  const commands = [];
  const go = (id, label, path, icon, moduleKey, keywords = []) => {
    if (moduleKey && (!isModuleVisible(moduleKey) || !canDo(moduleKey, 'view'))) return;
    commands.push({ id, group: 'Go to', label, keywords: ['go to', 'open', ...keywords], icon, run: () => navigate(path) });
  };
  const create = (id, label, path, icon, moduleKey, keywords = []) => {
    if (!isModuleVisible(moduleKey) || !canDo(moduleKey, 'create')) return;
    commands.push({ id, group: 'Create', label, keywords: ['new', 'add', 'create', ...keywords], icon, run: () => navigate(path) });
  };

  go('go-today', 'Today', '/', Sun, null, ['home', 'my day', 'overdue']);
  go('go-dashboard', 'Dashboard', '/dashboard', LayoutDashboard, null, ['metrics', 'stats']);
  go('go-accounts', 'Accounts', '/accounts', Building2, 'accounts', ['companies', 'customers']);
  go('go-contacts', 'Contacts', '/contacts', Users, 'contacts', ['people']);
  go('go-opportunities', 'Opportunities', '/opportunities', Target, 'opportunities', ['deals', 'pipeline']);
  go('go-rep-firms', 'Rep Firms', '/rep-firms', Briefcase, 'rep_firms', ['reps']);
  go('go-products', 'Products', '/products', Package, 'products', ['catalog', 'parts']);
  go('go-quotes', 'Quotes', '/quotes', FileText, 'quotes', ['quotations']);
  go('go-orders', 'Orders', '/orders', ShoppingCart, 'orders', ['purchase orders']);
  go('go-action-items', 'Action Items', '/action-items', CheckSquare, 'tasks', ['tasks', 'to do', 'todos', 'follow ups']);
  go('go-activities', 'Activities', '/activities', Activity, 'activities', ['timeline', 'notes', 'calls']);
  go('go-reports', 'Reports', '/reports', BarChart3, 'reports', ['charts', 'pipeline report', 'revenue']);
  if (isAdmin || hasPermission('view_all_accounts') || hasPermission('manage_settings')) {
    commands.push({ id: 'go-review', group: 'Go to', label: 'Executive Review', keywords: ['go to', 'weekly summary', 'review'], icon: ClipboardCheck, run: () => navigate('/executive-review') });
  }
  if (hasPermission('manage_settings')) {
    commands.push({ id: 'go-settings', group: 'Go to', label: 'Settings', keywords: ['go to', 'admin', 'preferences', 'users', 'stages'], icon: Settings, run: () => navigate('/settings') });
  }

  if (askAssistant && isModuleVisible('activities') && canDo('activities', 'create')) {
    commands.push({ id: 'capture', group: 'Create', label: 'Open the AI Assistant', keywords: ['ai', 'assistant', 'ask', 'log', 'call', 'meeting', 'email', 'note', 'activity', 'dictate', 'jot', 'capture'], icon: PenLine, run: () => askAssistant('', { send: false }) });
    commands.push({ id: 'meeting-writeup', group: 'Create', label: 'Write up a meeting', keywords: ['meeting', 'notes', 'report', 'write-up', 'writeup', 'recap', 'minutes', 'email'], icon: FileText, run: () => askAssistant('Write up my meeting with ', { send: false }) });
  }
  create('new-activity', 'Log an activity (form)', '/activities?new=1', Plus, 'activities', ['log', 'call', 'meeting', 'email', 'activity', 'form']);
  create('new-action-item', 'New action item', '/action-items?new=1', Plus, 'tasks', ['task', 'to do', 'follow up', 'reminder']);
  create('new-account', 'New account', '/accounts?new=1', Plus, 'accounts', ['company', 'customer']);
  create('new-contact', 'New contact', '/contacts?new=1', Plus, 'contacts', ['person', 'people']);
  create('new-opportunity', 'New opportunity', '/opportunities?new=1', Plus, 'opportunities', ['deal']);

  commands.push({
    id: 'toggle-theme', group: 'Actions', label: theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode',
    keywords: ['theme', 'dark mode', 'light mode', 'appearance'], icon: theme === 'dark' ? Sun : Moon, run: toggleTheme,
  });
  commands.push({ id: 'shortcuts', group: 'Actions', label: 'Keyboard shortcuts', keywords: ['help', 'keys', 'hotkeys'], icon: Keyboard, run: openShortcuts });

  return commands;
}
